/**
 * Query Persistence Hook - Automatically hydrate and persist React Query cache
 * Ensures data survives app restarts for offline access
 * 
 * CRITICAL: Hydration MUST complete before bootstrap components render
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  hydrateQueryCache,
  persistQueryCache,
  getAllCriticalQueryKeys,
} from '@/lib/queryPersistence';
import { logger } from '@/lib/logger';

// Global hydration state - accessible outside React
let globalHydrationComplete = false;
let hydrationPromise: Promise<void> | null = null;

/**
 * Check if cache hydration is complete
 * Can be called outside React components
 */
export function isHydrationComplete(): boolean {
  return globalHydrationComplete;
}

/**
 * Wait for hydration to complete
 * Useful for components that need cached data before rendering
 */
export function waitForHydration(): Promise<void> {
  if (globalHydrationComplete) return Promise.resolve();
  if (hydrationPromise) return hydrationPromise;
  // If hydration hasn't started, resolve immediately (will be called on next mount)
  return Promise.resolve();
}

/**
 * Hook to hydrate React Query cache from IndexedDB on app startup
 * Should be called once at the app root level
 * 
 * CRITICAL: This must complete BEFORE dashboard bootstrap queries run
 */
export function useQueryCacheHydration(): { isHydrated: boolean } {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const hydratedRef = useRef(false);
  const [isHydrated, setIsHydrated] = useState(globalHydrationComplete);

  useEffect(() => {
    if (!user?.id || hydratedRef.current) {
      // Already hydrated or no user - mark as complete
      if (user?.id) {
        globalHydrationComplete = true;
        setIsHydrated(true);
      }
      return;
    }
    
    // Only hydrate once per session
    hydratedRef.current = true;

    if (import.meta.env.DEV) {
      logger.log('[useQueryPersistence] cache_hydrate_start');
    }

    // Create promise for external waiters
    hydrationPromise = hydrateQueryCache(queryClient, user.id)
      .then(() => {
        globalHydrationComplete = true;
        setIsHydrated(true);
        if (import.meta.env.DEV) {
          logger.log('[useQueryPersistence] cache_hydrate_end');
        }
      })
      .catch((err) => {
        // Still mark as complete even on error to not block app
        globalHydrationComplete = true;
        setIsHydrated(true);
        logger.error('[useQueryPersistence] cache_hydrate_error', err);
      });
  }, [queryClient, user?.id]);

  return { isHydrated };
}

/**
 * Hook to persist React Query cache to IndexedDB after data fetches
 * Automatically persists critical queries when they change
 */
export function useQueryCachePersistence(): void {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user?.id) return;

    const userId = user.id;

    // Subscribe to query cache updates
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      // Only persist on successful fetches
      if (event?.type !== 'updated' || event?.action?.type !== 'success') return;

      // Debounce persistence to avoid too many writes
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
      }

      persistTimeoutRef.current = setTimeout(() => {
        const criticalKeys = getAllCriticalQueryKeys(userId);
        persistQueryCache(queryClient, userId, criticalKeys);
      }, 2000); // Wait 2s after last update before persisting
    });

    return () => {
      unsubscribe();
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
      }
    };
  }, [queryClient, user?.id]);
}

/**
 * Combined hook for both hydration and persistence
 * Use this at the app root level
 */
export function useOfflineQueryPersistence(): void {
  useQueryCacheHydration();
  useQueryCachePersistence();
}
